"use client";

import React from "react";
import { Building2, ShieldCheck, FileText, HardHat, CircleCheck as CheckCircle2, ArrowRight, MapPin, Award, Layers, Shield } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
const accreditations = [{
  name: "NASC",
  title: "NASC Full Member",
  desc: "Strict safety audits and adherence to TG20:21 & SG4 guidelines."
}, {
  name: "CHAS",
  title: "CHAS Accredited",
  desc: "Demonstrated compliance across health, safety, and risk management."
}];
const capabilities = [{
  icon: ShieldCheck,
  title: "Safety-led delivery",
  text: "Every project operates under site-specific RAMS, CISRS-qualified scaffolders, and regular statutory 7-day inspections."
}, {
  icon: FileText,
  title: "Complete documentation",
  text: "Full handover certificates, design calculations, and prompt compliance packs for clerk of works and safety officers."
}, {
  icon: HardHat,
  title: "Procurement-ready coordination",
  text: "Transparent billing, reliable timeline management, and dedicated supervision tailored to public-sector and housing frameworks."
}];
const relatedServices = [{
  title: "Commercial Scaffolding",
  desc: "High-capacity independent scaffolds, facade access, and pedestrian protection gantries for civic and commercial estates.",
  to: "/CommercialScaffoldingPage"
}, {
  title: "Specialist Access Scaffolding",
  desc: "Cantilevered, suspended, and engineered scaffolding designed for complex public buildings and heritage infrastructure.",
  to: "/SpecialistAccessScaffoldingPage"
}, {
  title: "Edge Protection",
  desc: "Compliant perimeter guardrails and fall-prevention systems for flat roofs, council repairs, and maintenance teams.",
  to: "/EdgeProtectionPage"
}];
const coverageAreas = ["Berkshire", "Surrey", "Hampshire", "Buckinghamshire", "London", "South East & Home Counties"];
export default function PublicSector() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="publicsectorscaffoldingoverview" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Answer-First Header & Overview */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start mb-16">
          <motion.div initial={{
          opacity: 0,
          y: shouldReduceMotion ? 0 : 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} className="lg:col-span-7 flex flex-col gap-5">
            <div className="flex items-center gap-2">
              <Badge variant="secondary" className="gap-1.5 px-3 py-1 text-xs font-semibold">
                <Building2 className="size-3.5 text-primary" />
                Local Authorities & Housing Associations
              </Badge>
              <Badge variant="outline" className="text-xs">
                Procurement Ready
              </Badge>
            </div>

            <h1 className="font-default text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground">
              Scaffolding for councils & public sector
            </h1>

            <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
              Public-sector projects demand absolute safety compliance, rigorous documentation, and dependable contractor coordination. BMF Scaffolding Ltd delivers procurement-ready access solutions for local authorities, schools, healthcare trusts, and public housing authorities across the South East.
            </p>

            <div className="flex flex-wrap gap-4 pt-2">
              <Button asChild size="lg" className="shadow-sm">
                <Link to="/ContactRequestQuotePage">
                  Request tender pack
                  <ArrowRight className="size-4 ml-1" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg">
                <Link to="/HealthSafetyAccreditationsPage">
                  View accreditations
                </Link>
              </Button>
            </div>
          </motion.div>

          {/* Accreditations Strip / Summary Card */}
          <motion.div initial={{
          opacity: 0,
          y: shouldReduceMotion ? 0 : 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.15
        }} className="lg:col-span-5">
            <Card className="border-border bg-card shadow-sm">
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2 text-primary font-semibold text-sm">
                  <Award className="size-4.5" />
                  <span>Verified contractor accreditations</span>
                </div>
                <CardTitle className="text-xl font-default pt-1">
                  Confirmed safety standards
                </CardTitle>
                <CardDescription>
                  BMF Scaffolding holds verified industry accreditations required for public tenders.
                </CardDescription>
              </CardHeader>
              <CardContent className="flex flex-col gap-4">
                {accreditations.map((acc, index) => <div key={acc.name} data-index={index} className="flex items-start gap-3 p-3 rounded-lg bg-muted/40 border border-border/60">
                    <CheckCircle2 className="size-5 text-primary shrink-0 mt-0.5" />
                    <div>
                      <span className="font-semibold text-sm text-foreground block">
                        {acc.title}
                      </span>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {acc.desc}
                      </p>
                    </div>
                  </div>)}
              </CardContent>
            </Card>
          </motion.div>
        </div>

        <Separator className="my-12 opacity-60" />

        {/* What Public Sector Clients Need / How BMF Supports */}
        <div className="mb-16">
          <div className="max-w-3xl mb-8">
            <h2 className="font-default text-2xl sm:text-3xl font-bold text-foreground mb-3">
              How BMF supports public-sector teams
            </h2>
            <p className="text-muted-foreground text-sm sm:text-base">
              From planned municipal maintenance to responsive repairs, we provide the structured management and documented safety standards public procurement teams require.
            </p>
          </div>

          <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.2
        }} className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {capabilities.map((cap, i) => {
            const Icon = cap.icon;
            return <motion.div key={cap.title} variants={itemVariants} data-index={i}>
                  <Card className="h-full border-border bg-card shadow-xs transition-[border-color,box-shadow] duration-200 hover:border-primary/40 hover:shadow-sm">
                    <CardHeader className="pb-2">
                      <div className="size-11 rounded-lg bg-primary/10 text-primary flex items-center justify-center mb-3">
                        <Icon className="size-5" />
                      </div>
                      <CardTitle className="text-lg font-default">{cap.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {cap.text}
                      </p>
                    </CardContent>
                  </Card>
                </motion.div>;
          })}
          </motion.div>
        </div>

        {/* Relevant Services */}
        <div className="mb-16">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-8 gap-4">
            <div>
              <div className="flex items-center gap-1.5 text-xs font-semibold text-primary uppercase tracking-wider mb-1">
                <Layers className="size-3.5" />
                Service Scope
              </div>
              <h2 className="font-default text-2xl sm:text-3xl font-bold text-foreground">
                Specialist services for public infrastructure
              </h2>
            </div>
            <Button asChild variant="outline" size="sm" className="self-start sm:self-auto">
              <Link to="/ProjectsCaseStudiesPage">
                View completed projects
              </Link>
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {relatedServices.map((svc, idx) => <Card key={svc.title} data-index={idx} className="flex flex-col justify-between border-border bg-card shadow-xs transition-[border-color,box-shadow] duration-200 hover:border-primary/40 hover:shadow-sm">
                <CardHeader>
                  <CardTitle className="text-lg font-default">{svc.title}</CardTitle>
                  <CardDescription className="text-sm pt-2 leading-relaxed">
                    {svc.desc}
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-0">
                  <Button asChild variant="link" className="p-0 h-auto font-semibold gap-1 text-primary">
                    <Link to={svc.to}>
                      Service specifications
                      <ArrowRight className="size-3.5" />
                    </Link>
                  </Button>
                </CardContent>
              </Card>)}
          </div>
        </div>

        {/* Geographic Coverage */}
        <div className="rounded-xl border border-border bg-card p-6 sm:p-8 shadow-xs">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-center">
            <div className="lg:col-span-5 flex flex-col gap-2">
              <div className="flex items-center gap-2 text-primary font-semibold text-xs tracking-wider uppercase">
                <MapPin className="size-4" />
                Regional Operational Footprint
              </div>
              <h3 className="font-default text-xl font-bold text-foreground">
                Geographic coverage for local authorities
              </h3>
              <p className="text-sm text-muted-foreground">
                We provide prompt site surveys, dedicated yard logistics, and reliable deployment across key councils and boroughs.
              </p>
            </div>

            <div className="lg:col-span-7 flex flex-wrap gap-2.5 items-center">
              {coverageAreas.map((area, areaIdx) => <div key={area} data-index={areaIdx} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-muted text-foreground text-xs sm:text-sm font-medium border border-border/80">
                  <Shield className="size-3 text-primary" />
                  <span>{area}</span>
                </div>)}
              <div className="w-full pt-2">
                <Button asChild variant="link" className="px-0 text-xs text-primary font-semibold">
                  <Link to="/AreasWeCoverPage">
                    View complete regional coverage details
                    <ArrowRight className="size-3 ml-1" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}